import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from './firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, getDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { Truck, X, Printer, Eye, Wallet, ExternalLink } from 'lucide-react';
import CourierLedger from './CourierLedger';
import { codAmountFor, codFeeOf, codFeeFor, formatFee } from './dueUtil';
import { printSalesInvoice } from './printInvoice';

const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

const STEADFAST_CREATE = '/api/courier/steadfast/create-order';
const PATHAO_CREATE = '/api/courier/pathao/create-order';
const PATHAO_CITIES = '/api/courier/pathao/cities';
const PATHAO_ZONES = '/api/courier/pathao/zones';
const PATHAO_AREAS = '/api/courier/pathao/areas';
const PATHAO_PRICE_PLAN = '/api/courier/pathao/price-plan';
const PATHAO_STATUS = '/api/courier/pathao/status';
const STEADFAST_STATUS = '/api/courier/steadfast/status';
const COURIER_STATUS = '/api/courier/status';

const Courier = () => {
  const [tab, setTab] = useState('bookings'); // 'bookings' | 'ledger'
  const [secret, setSecret] = useState('');
  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('not_booked'); // 'all' | 'not_booked' | 'pending' | 'hold' | 'delivered' | 'returned' | 'cancelled'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [connected, setConnected] = useState({ steadfast: false, pathao: false, pathaoEnv: 'sandbox' });
  const [checkingConnection, setCheckingConnection] = useState(false);

  const [bookingSale, setBookingSale] = useState(null);
  const [provider, setProvider] = useState('steadfast');
  const [form, setForm] = useState({});
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');

  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [estimatedFee, setEstimatedFee] = useState(null);
  const [loadingFee, setLoadingFee] = useState(false);

  const [viewInvoice, setViewInvoice] = useState(null);
  const [bookedResult, setBookedResult] = useState(null); // sale + courierBooking, shown right after a successful Book
  const [checkingStatusId, setCheckingStatusId] = useState(null); // invoiceId currently being checked, to disable that one button

  // Courier booking reuses the same APP_SHARED_SECRET already set up on the
  // Website Orders page -- the Cloudflare Worker only has one shared secret.
  useEffect(() => {
    getDoc(doc(db, 'settings', 'websiteSync')).then((snap) => {
      if (snap.exists()) setSecret(snap.data().secret || '');
    });
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'sales'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => setSales(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const checkConnection = useCallback(async () => {
    if (!secret) return;
    setCheckingConnection(true);
    try {
      const res = await fetch(COURIER_STATUS, { headers: { 'X-App-Secret': secret } });
      const data = await res.json();
      if (res.ok) setConnected(data);
    } catch (e) { /* silently ignore -- shown as "not connected" below */ }
    setCheckingConnection(false);
  }, [secret]);

  useEffect(() => { checkConnection(); }, [checkConnection]);

  // Buckets the courier's free-text status into the tabs Sohan filters by. Order
  // matters: "Delivery_Failed" and "Assigned_for_Delivery" both contain "deliver", so
  // only an exact "delivered" counts as delivered, and failures fall to On Hold.
  const getStatusBucket = (sale) => {
    if (!sale.courierBooking) return 'not_booked';
    const s = (sale.courierBooking.status || '').toLowerCase();
    if (s.includes('cancel')) return 'cancelled';
    if (s.includes('return')) return 'returned';
    if (s.includes('hold') || s.includes('fail')) return 'hold';
    if (s.includes('delivered')) return 'delivered';
    // Everything from pickup to the last-mile hub means the parcel is physically with
    // the courier -- that's what Sohan wants to see on the day they collect.
    if (/pick|transit|hub|sorting|assigned|received/.test(s)) return 'picked';
    return 'pending';
  };

  const filteredSales = useMemo(() => {
    const term = search.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    return sales.filter((s) => {
      // Walk-in/in-shop sales marked "Direct Sale" never need a courier -- keep them
      // out of this queue entirely. Older sales made before this field existed have no
      // deliveryMethod at all, so they still show up here (nothing about them changes).
      if (s.deliveryMethod === 'direct') return false;
      if (statusFilter !== 'all' && getStatusBucket(s) !== statusFilter) return false;
      if (from || to) {
        const d = s.createdAt?.toDate ? s.createdAt.toDate() : null;
        if (!d) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      if (!term) return true;
      return (s.name || '').toLowerCase().includes(term) ||
        (s.phone || '').toLowerCase().includes(term) ||
        (s.invoiceId || '').toLowerCase().includes(term);
    });
  }, [sales, search, statusFilter, dateFrom, dateTo]);

  const openBooking = (sale) => {
    setBookingSale(sale);
    setBookedResult(null);
    // Website Orders-এ যে কুরিয়ার বেছে রাখা হয়েছিল সেটাই আগে; সেটা এখন চালু না থাকলে আগের ডিফল্ট
    const preferred = sale.preferredCourier;
    setProvider(preferred && connected[preferred] ? preferred : (connected.steadfast ? 'steadfast' : 'pathao'));
    setError('');
    setCities([]); setZones([]); setAreas([]); setEstimatedFee(null);
    setForm({
      recipient_name: sale.name || '',
      recipient_phone: sale.phone || '',
      recipient_address: sale.address || '',
      cod_amount: codAmountFor(sale),
      note: '',
      recipient_city: '', recipient_zone: '', recipient_area: '',
      item_description: (sale.items || []).map((it) => it.name).join(', ')
    });
  };

  const closeBooking = () => { setBookingSale(null); setBookedResult(null); };

  useEffect(() => {
    if (!bookingSale || provider !== 'pathao' || !secret || !connected.pathao) return;
    setLoadingLocation(true);
    fetch(PATHAO_CITIES, { headers: { 'X-App-Secret': secret } })
      .then((r) => r.json()).then((d) => setCities(Array.isArray(d) ? d : []))
      .catch(() => setCities([]))
      .finally(() => setLoadingLocation(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingSale, provider]);

  const onCityChange = async (cityId) => {
    setForm((f) => ({ ...f, recipient_city: cityId, recipient_zone: '', recipient_area: '' }));
    setZones([]); setAreas([]); setEstimatedFee(null);
    if (!cityId) return;
    setLoadingLocation(true);
    try {
      const res = await fetch(`${PATHAO_ZONES}?cityId=${cityId}`, { headers: { 'X-App-Secret': secret } });
      setZones(await res.json());
    } catch (e) { setZones([]); }
    setLoadingLocation(false);
  };

  const onZoneChange = async (zoneId) => {
    setForm((f) => ({ ...f, recipient_zone: zoneId, recipient_area: '' }));
    setAreas([]); setEstimatedFee(null);
    if (!zoneId) return;
    setLoadingLocation(true);
    try {
      const res = await fetch(`${PATHAO_AREAS}?zoneId=${zoneId}`, { headers: { 'X-App-Secret': secret } });
      setAreas(await res.json());
    } catch (e) { setAreas([]); }
    setLoadingLocation(false);

    // Best-effort delivery-fee estimate -- silently skipped if it fails, since the
    // booking itself doesn't depend on it.
    setLoadingFee(true);
    try {
      const res = await fetch(PATHAO_PRICE_PLAN, {
        method: 'POST',
        headers: { 'X-App-Secret': secret, 'content-type': 'application/json' },
        body: JSON.stringify({ recipient_city: form.recipient_city, recipient_zone: zoneId })
      });
      const data = await res.json();
      setEstimatedFee(res.ok ? data.price : null);
    } catch (e) { setEstimatedFee(null); }
    setLoadingFee(false);
  };

  const submitBooking = async () => {
    if (!bookingSale) return;
    setBooking(true);
    setError('');
    try {
      let res, data, newCourierBooking;
      if (provider === 'steadfast') {
        res = await fetch(STEADFAST_CREATE, {
          method: 'POST',
          headers: { 'X-App-Secret': secret, 'content-type': 'application/json' },
          body: JSON.stringify({
            invoice: bookingSale.invoiceId,
            recipient_name: form.recipient_name,
            recipient_phone: form.recipient_phone,
            recipient_address: form.recipient_address,
            cod_amount: form.cod_amount,
            note: form.note
          })
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Booking failed');
        newCourierBooking = {
          provider: 'steadfast', consignmentId: data.consignmentId, trackingCode: data.trackingCode,
          status: data.status || 'in_review', codAmount: Number(form.cod_amount) || 0, bookedAt: serverTimestamp()
        };
      } else {
        if (!form.recipient_city || !form.recipient_zone) throw new Error('Please select City and Zone.');
        res = await fetch(PATHAO_CREATE, {
          method: 'POST',
          headers: { 'X-App-Secret': secret, 'content-type': 'application/json' },
          body: JSON.stringify({
            merchant_order_id: bookingSale.invoiceId,
            recipient_name: form.recipient_name,
            recipient_phone: form.recipient_phone,
            recipient_address: form.recipient_address,
            recipient_city: form.recipient_city,
            recipient_zone: form.recipient_zone,
            recipient_area: form.recipient_area || undefined,
            amount_to_collect: form.cod_amount,
            item_description: form.item_description,
            item_weight: bookingSale.itemWeight || 0.5
          })
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Booking failed');
        newCourierBooking = {
          provider: 'pathao',
          consignmentId: data.consignmentId,
          status: data.status || 'Pending',
          deliveryFee: data.deliveryFee ?? null, // what Pathao will actually deduct for this parcel
          // Pathao-কে যত COD তুলতে বলা হলো -- COD Fee (১%) এর ভিত্তি। পরে Receive করলে sale-এর dueAmount ০ হয়ে
          // গেলেও এখান থেকে ফি-টা হিসাবে থেকে যায় (dueUtil.js-এর codFeeFor দেখুন)
          codAmount: Number(form.cod_amount) || 0,
          bookedAt: serverTimestamp()
        };
      }
      // Test bookings are saved like real ones, just flagged -- otherwise the order
      // stays "Not booked" after booking it and there's no way to watch it move through
      // Pending/Picked/Delivered, which is the whole point of testing. "Clear Test
      // Bookings" wipes them before going live.
      const isTestBooking = newCourierBooking.provider === 'pathao' && connected.pathaoEnv !== 'live';
      if (isTestBooking) newCourierBooking.isTest = true;
      await updateDoc(doc(db, 'sales', bookingSale.id), { courierBooking: newCourierBooking });
      // Show a Print/Skip choice right away instead of just closing -- Sohan wants the
      // shipping label offered immediately after a successful booking, not buried back
      // in the table row. Use the (possibly edited-in-modal) recipient/COD fields that
      // actually went to the courier, not the original sale doc -- otherwise a phone/address
      // fix made here right before booking wouldn't show up on the printed label.
      setBookedResult({
        ...bookingSale,
        name: form.recipient_name,
        phone: form.recipient_phone,
        address: form.recipient_address,
        codAmountUsed: form.cod_amount,
        courierBooking: newCourierBooking,
        isTestBooking
      });
    } catch (e) {
      setError(e.message);
    }
    setBooking(false);
  };

  // One call to the courier's own status API -- shared by the single "Check Status"
  // button and the "Refresh All" bulk loop below.
  const fetchCourierStatus = async (courierBooking) => {
    const endpoint = courierBooking.provider === 'pathao' ? PATHAO_STATUS : STEADFAST_STATUS;
    const res = await fetch(`${endpoint}?consignmentId=${encodeURIComponent(courierBooking.consignmentId)}`, {
      headers: { 'X-App-Secret': secret }
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Status check failed');
    return { status: data.status || 'Unknown', paymentStatus: data.paymentStatus || null, invoiceId: data.invoiceId || null };
  };

  // Lets Sohan verify for himself that a booking really reached the courier's system,
  // instead of just trusting the one-time "Booked Successfully" popup -- and pulls the
  // real delivery status (Pending/On Hold/Delivered/Cancelled/Returned) plus COD payment
  // status onto the sale doc, so the table doesn't stay stuck on whatever it was at
  // booking time. Test Mode bookings never had a sale doc write, so nothing to persist there.
  const checkStatus = async (sale) => {
    const courierBooking = sale.courierBooking;
    if (!courierBooking?.consignmentId) return;
    setCheckingStatusId(sale.invoiceId);
    try {
      const { status, paymentStatus, invoiceId } = await fetchCourierStatus(courierBooking);
      if (status !== courierBooking.status || paymentStatus !== (courierBooking.paymentStatus || null) || invoiceId !== (courierBooking.invoiceId || null)) {
        await updateDoc(doc(db, 'sales', sale.id), {
          'courierBooking.status': status,
          'courierBooking.paymentStatus': paymentStatus,
          'courierBooking.invoiceId': invoiceId
        });
      }
      alert(
        `Courier: ${courierBooking.provider === 'pathao' ? 'Pathao' : 'Steadfast'}\n` +
        `Consignment ID: ${courierBooking.consignmentId}\n` +
        `Delivery Status: ${status}` +
        (courierBooking.deliveryFee != null ? `\nPathao Delivery Fee: ৳ ${formatTk(courierBooking.deliveryFee)}` : '') +
        (paymentStatus ? `\nPayment Status: ${paymentStatus}` : '') +
        `\nInvoice: ${invoiceId || 'not invoiced by Pathao yet'}`
      );
    } catch (e) {
      alert('Could not check status: ' + e.message);
    }
    setCheckingStatusId(null);
  };

  // Bulk version for the "Refresh All Statuses" button -- runs sequentially (not
  // Promise.all) so a small shop's handful of daily bookings don't all hit the
  // courier's API in one burst.
  const [refreshingAll, setRefreshingAll] = useState(false);
  const refreshAllStatuses = async () => {
    const bookedSales = sales.filter((s) => s.courierBooking?.consignmentId);
    if (bookedSales.length === 0) return alert('No booked orders to refresh yet.');
    setRefreshingAll(true);
    let updated = 0, failed = 0;
    for (const sale of bookedSales) {
      try {
        const { status, paymentStatus, invoiceId } = await fetchCourierStatus(sale.courierBooking);
        if (status !== sale.courierBooking.status || paymentStatus !== (sale.courierBooking.paymentStatus || null) || invoiceId !== (sale.courierBooking.invoiceId || null)) {
          await updateDoc(doc(db, 'sales', sale.id), {
            'courierBooking.status': status,
            'courierBooking.paymentStatus': paymentStatus,
            'courierBooking.invoiceId': invoiceId
          });
          updated++;
        }
      } catch (e) {
        failed++;
      }
    }
    setRefreshingAll(false);
    alert(`Checked ${bookedSales.length} booked order(s).\nUpdated: ${updated}${failed ? `\nFailed to check: ${failed}` : ''}`);
  };

  // Test bookings are saved so the workflow can be watched end to end, so there has to
  // be a way to wipe them before real bookings start -- otherwise fake consignments sit
  // in the table forever looking real.
  const testBookings = sales.filter((s) => s.courierBooking?.isTest);
  const clearTestBookings = async () => {
    if (!window.confirm(`Remove the courier booking from ${testBookings.length} test order(s)? They'll go back to "Not booked". Real bookings are not touched.`)) return;
    for (const sale of testBookings) {
      await updateDoc(doc(db, 'sales', sale.id), { courierBooking: deleteField() });
    }
  };

  // Pathao-তে বুক করার সময় COD টাকার ১% ফি -- ডেলিভারি ফি-র বাইরে আলাদা কাটা, দোকানের খরচ
  const modalCodFee = bookingSale && provider === 'pathao' ? codFeeOf(form.cod_amount) : 0;

  const printLabel = (sale) => {
    const b = sale.courierBooking || {};
    const win = window.open('', '_blank', 'width=420,height=600');
    if (!win) return alert('Pop-up is blocked -- please allow pop-ups for this site in your browser.');
    win.document.write(`
      <html><head><title>Label - ${sale.invoiceId}</title>
      <style>
        body{font-family:sans-serif;padding:16px;}
        h2{margin:0 0 4px;} p{margin:4px 0;font-size:13px;}
        .box{border:2px solid #000;border-radius:8px;padding:14px;}
        .big{font-size:16px;font-weight:bold;}
      </style></head><body>
        <div class="box">
          <h2>PB KIZUNA COMPANY LIMITED</h2>
          <p>H-10, R-2, Section-2, Block-D, Aftabnagar, Dhaka-1212 | 01884-711844</p>
          <hr/>
          <p class="big">Courier: ${b.provider === 'pathao' ? 'Pathao' : 'Steadfast'}</p>
          <p class="big">Consignment ID: ${b.consignmentId || '-'}</p>
          ${b.trackingCode ? `<p>Tracking Code: ${b.trackingCode}</p>` : ''}
          <hr/>
          <p><b>To:</b> ${sale.name || ''}</p>
          <p><b>Phone:</b> ${sale.phone || ''}</p>
          <p><b>Address:</b> ${sale.address || ''}</p>
          <hr/>
          <p class="big">COD: ৳ ${formatTk(sale.codAmountUsed != null ? sale.codAmountUsed : codAmountFor(sale))}</p>
          <p>Invoice: #${sale.invoiceId}</p>
        </div>
        <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div style={{ padding: '20px', background: '#f4f7fe', minHeight: '100vh' }}>
      <div className="white-box" style={{ padding: '30px', borderRadius: '25px', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ margin: 0, color: '#333' }}><Truck size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />Courier Sync</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', background: connected.steadfast ? '#eafff3' : '#f4f4f4', color: connected.steadfast ? '#218c61' : '#999' }}>
              Steadfast {connected.steadfast ? '✅ Connected' : '⚠️ Not connected'}
            </span>
            <span style={{ padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', background: connected.pathao ? '#eafff3' : '#f4f4f4', color: connected.pathao ? '#218c61' : '#999' }}>
              Pathao {connected.pathao ? '✅ Connected' : '⚠️ Not connected'}
            </span>
            {connected.pathao && (
              <span style={{
                padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold',
                background: connected.pathaoEnv === 'live' ? '#ffeded' : '#fff3cd',
                color: connected.pathaoEnv === 'live' ? '#c0392b' : '#8a6d3b'
              }}>
                {connected.pathaoEnv === 'live' ? '🔴 Pathao LIVE — Real Booking' : '🧪 Pathao TEST MODE — No Real Booking'}
              </span>
            )}
            {/* Pathao-র API-তে OTP/Notification দেখার কোনো endpoint নেই, তাই অ্যাপের ভেতরে আনা যায় না --
                শুধু Pathao-র merchant panel-এর Notifications পেজ নতুন ট্যাবে খোলার শর্টকাট।
                Test/Live যা-ই হোক এটা আসল merchant একাউন্টের পেজ, তাই connected-এর উপর নির্ভর করে না। */}
            <a
              href="https://merchant.pathao.com/courier/news"
              target="_blank"
              rel="noopener noreferrer"
              title="Opens Pathao's Notifications page in a new tab — the OTP tab is next to Notice"
              style={{ padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', background: '#fff', color: '#4a00e0', border: '1px solid #4a00e0', textDecoration: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
            >
              <ExternalLink size={13} /> Pathao Notifications (OTP)
            </a>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button
            onClick={() => setTab('bookings')}
            style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${tab === 'bookings' ? '#4a00e0' : '#ddd'}`, background: tab === 'bookings' ? '#4a00e0' : '#fff', color: tab === 'bookings' ? '#fff' : '#333', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Truck size={14} /> Bookings
          </button>
          <button
            onClick={() => setTab('ledger')}
            style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${tab === 'ledger' ? '#4a00e0' : '#ddd'}`, background: tab === 'ledger' ? '#4a00e0' : '#fff', color: tab === 'ledger' ? '#fff' : '#333', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Wallet size={14} /> Payment Ledger
          </button>
        </div>

        {tab === 'ledger' && <CourierLedger />}

        {tab === 'bookings' && (<>

        {connected.pathao && connected.pathaoEnv !== 'live' && (
          <div style={{ background: '#fff3cd', color: '#8a6d3b', padding: '12px 16px', borderRadius: '10px', marginBottom: '18px', fontSize: '13px' }}>
            🧪 Pathao is in <b>Test/Sandbox Mode</b> — bookings made here go to Pathao's test system only, not your real Kizuna Clothing account. Nothing here affects real orders or money.
            {testBookings.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <b>{testBookings.length}</b> test booking(s) are saved so you can follow them through Pending → Picked Up → Delivered. They're marked <b>TEST</b> in the table.
                <button
                  onClick={clearTestBookings}
                  style={{ marginLeft: '10px', padding: '5px 12px', borderRadius: '8px', border: '1px solid #c0392b', background: '#fff', color: '#c0392b', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                >
                  Clear Test Bookings
                </button>
              </div>
            )}
          </div>
        )}

        {/* Live-এ যাওয়ার পরেও পুরনো টেস্ট বুকিং থেকে গেলে মোছার উপায় যেন হারিয়ে না যায় --
            এগুলোর consignment ID আসল Pathao-তে নেই, তাই Refresh All-এ এরর দেবে */}
        {connected.pathaoEnv === 'live' && testBookings.length > 0 && (
          <div style={{ background: '#ffebee', color: '#c0392b', padding: '12px 16px', borderRadius: '10px', marginBottom: '18px', fontSize: '13px' }}>
            Pathao is now <b>Live</b>, but <b>{testBookings.length}</b> old test booking(s) from Test Mode are still saved. They don't exist in your real Pathao account — clear them.
            <button
              onClick={clearTestBookings}
              style={{ marginLeft: '10px', padding: '5px 12px', borderRadius: '8px', border: '1px solid #c0392b', background: '#fff', color: '#c0392b', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
            >
              Clear Test Bookings
            </button>
          </div>
        )}

        {!secret && (
          <div style={{ background: '#fff3cd', color: '#8a6d3b', padding: '12px 16px', borderRadius: '10px', marginBottom: '18px', fontSize: '13px' }}>
            Set the Shared Secret on the <b>Website Orders</b> page first — Courier Sync uses that same secret.
          </div>
        )}

        {/* PATHAO_ENV=live হলে worker শুধু আসল (live) secret-গুলো খোঁজে -- তখন sandbox-এর
            নাম দেখালে মনে হয় সেগুলো হারিয়ে গেছে, অথচ আসলে live-এর ৪টা যোগ করা বাকি */}
        {!connected.steadfast && !connected.pathao && !checkingConnection && secret && (
          <div style={{ background: '#fff3cd', color: '#8a6d3b', padding: '12px 16px', borderRadius: '10px', marginBottom: '18px', fontSize: '13px' }}>
            {connected.pathaoEnv === 'live' ? (
              <>
                Pathao is set to <b>Live</b> (<code>PATHAO_ENV</code> = live), but your real Pathao account's login isn't added yet.
                Add <code>PATHAO_CLIENT_ID</code>/<code>PATHAO_CLIENT_SECRET</code>/<code>PATHAO_USERNAME</code>/<code>PATHAO_PASSWORD</code> as
                Cloudflare Worker secrets (Client ID and Client Secret are on Pathao's <b>Developer's API</b> page). The test-mode
                <code> PATHAO_SANDBOX_*</code> secrets are still there but aren't used in Live mode.
              </>
            ) : (
              <>
                No courier connected yet. Add <code>STEADFAST_API_KEY</code>/<code>STEADFAST_SECRET_KEY</code> or
                <code> PATHAO_SANDBOX_CLIENT_ID</code>/<code>PATHAO_SANDBOX_CLIENT_SECRET</code>/<code>PATHAO_SANDBOX_USERNAME</code>/<code>PATHAO_SANDBOX_PASSWORD</code> (test mode) as Cloudflare Worker secrets.
              </>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="🔍 Search by Name / Phone / Invoice..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="input-field" style={{ width: '280px', marginBottom: 0 }}
          />
          <label style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: '6px' }}>
            From
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-field" style={{ marginBottom: 0, padding: '8px' }} />
          </label>
          <label style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: '6px' }}>
            To
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-field" style={{ marginBottom: 0, padding: '8px' }} />
          </label>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '12px' }}>
              Clear Dates
            </button>
          )}
          <button
            onClick={refreshAllStatuses}
            disabled={refreshingAll}
            title="Ask the courier for the latest delivery status of every booked order"
            style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #4a00e0', background: '#fff', color: '#4a00e0', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', opacity: refreshingAll ? 0.6 : 1 }}
          >
            {refreshingAll ? '🔄 Refreshing...' : '🔄 Refresh All Statuses'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap' }}>
          {[
            { key: 'not_booked', label: 'Not Booked' },
            { key: 'pending', label: 'Pending' },
            { key: 'picked', label: 'Picked Up' },
            { key: 'hold', label: 'On Hold' },
            { key: 'delivered', label: 'Delivered' },
            { key: 'returned', label: 'Returned' },
            { key: 'cancelled', label: 'Cancelled' },
            { key: 'all', label: 'All' }
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              style={{
                padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer',
                border: `1px solid ${statusFilter === t.key ? '#4a00e0' : '#ddd'}`,
                background: statusFilter === t.key ? '#4a00e0' : '#fff',
                color: statusFilter === t.key ? '#fff' : '#333'
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
              <th style={{ padding: '15px' }}>Invoice</th>
              <th>Customer</th>
              <th>COD Amount</th>
              <th>Courier Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredSales.slice(0, 100).map((sale) => (
              <tr key={sale.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                <td style={{ padding: '15px' }}>
                  <button
                    onClick={() => setViewInvoice(sale)}
                    title="View Invoice"
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: '#4a00e0', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <Eye size={14} /> #{sale.invoiceId}
                  </button>
                </td>
                <td>
                  {sale.name || 'Walk-In'}
                  <div style={{ fontSize: '11px', color: '#999' }}>{sale.phone}</div>
                </td>
                <td>
                  ৳ {formatTk(codAmountFor(sale))}
                  {/* আগেই পুরো টাকা পাওয়া গেছে — কুরিয়ারকে কিছু তুলতে বলা যাবে না */}
                  {codAmountFor(sale) === 0 && (
                    <div style={{ fontSize: '10px', color: '#218c61', fontWeight: 'bold' }}>Prepaid</div>
                  )}
                </td>
                <td>
                  {sale.courierBooking ? (
                    <>
                      <span style={{ padding: '4px 10px', background: '#eafff3', color: '#218c61', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px' }}>
                        {sale.courierBooking.provider === 'pathao' ? 'Pathao' : 'Steadfast'} — {sale.courierBooking.status}
                        {sale.courierBooking.paymentStatus ? ` · Payment: ${sale.courierBooking.paymentStatus}` : ''}
                      </span>
                      {sale.courierBooking.isTest && (
                        <span style={{ marginLeft: '6px', padding: '3px 7px', background: '#fff3cd', color: '#8a6d3b', borderRadius: '6px', fontWeight: 'bold', fontSize: '10px' }}>TEST</span>
                      )}
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                        {sale.courierBooking.deliveryFee != null && <>Fee: ৳ {formatFee(sale.courierBooking.deliveryFee)} · </>}
                        {codFeeFor(sale) > 0 && <>COD fee: ৳ {formatFee(codFeeFor(sale))} · </>}
                        {sale.courierBooking.invoiceId
                          ? <span style={{ color: '#218c61' }}>Invoiced: {sale.courierBooking.invoiceId}</span>
                          : <span>Not invoiced yet</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ padding: '4px 10px', background: '#f4f4f4', color: '#999', borderRadius: '8px', fontSize: '12px' }}>Not booked</span>
                      {sale.preferredCourier && (
                        <div style={{ fontSize: '11px', color: '#4a00e0', fontWeight: 'bold', marginTop: '4px' }}>
                          Send via {sale.preferredCourier === 'pathao' ? 'Pathao' : 'Steadfast'}
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {sale.courierBooking ? (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button onClick={() => printLabel(sale)} style={{ padding: '6px 12px', background: '#333', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' }}>
                        <Printer size={14} /> Print Label
                      </button>
                      <button
                        onClick={() => checkStatus(sale)}
                        disabled={checkingStatusId === sale.invoiceId}
                        style={{ padding: '6px 12px', background: '#fff', color: '#4a00e0', border: '1px solid #4a00e0', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', opacity: checkingStatusId === sale.invoiceId ? 0.6 : 1 }}
                      >
                        {checkingStatusId === sale.invoiceId ? 'Checking...' : 'Check Status'}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => openBooking(sale)} className="btn-confirm" style={{ padding: '6px 14px', fontSize: '12px' }}>
                      Book
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filteredSales.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No orders found.</td></tr>
            )}
          </tbody>
        </table>

        </>)}
      </div>

      {bookingSale && (
        // No onClick-to-close on this overlay -- an accidental click just outside the
        // form while editing recipient phone/address was closing the modal and silently
        // discarding the edit (Sohan lost his corrected phone number this way). Only the
        // X button and Skip/Done button close it now.
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
          <div style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '450px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={closeBooking} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>

            {bookedResult ? (
              <>
                <h3 style={{ marginTop: 0, color: '#218c61' }}>✅ Booked Successfully</h3>
                {bookedResult.isTestBooking && (
                  <p style={{ fontSize: '12px', color: '#8a6d3b', background: '#fff3cd', padding: '8px 10px', borderRadius: '8px' }}>
                    🧪 This was a <b>Test Mode</b> booking — it went to Pathao's test system, not your real account. It's saved and marked <b>TEST</b> so you can follow it through the tabs; clear it later with "Clear Test Bookings".
                  </p>
                )}
                <p style={{ fontSize: '13px', color: '#666' }}>
                  #{bookedResult.invoiceId} has been booked with {bookedResult.courierBooking.provider === 'pathao' ? 'Pathao' : 'Steadfast'}
                  {bookedResult.courierBooking.consignmentId ? ` (Consignment ID: ${bookedResult.courierBooking.consignmentId})` : ''}.
                </p>
                <p style={{ fontSize: '13px', color: '#666' }}>Print a shipping label now to put on the packet, or skip and just keep it saved.</p>
                <button
                  onClick={() => checkStatus(bookedResult)}
                  disabled={checkingStatusId === bookedResult.invoiceId}
                  style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '10px', border: '1px solid #4a00e0', background: '#fff', color: '#4a00e0', cursor: 'pointer', fontSize: '13px', opacity: checkingStatusId === bookedResult.invoiceId ? 0.6 : 1 }}
                >
                  {checkingStatusId === bookedResult.invoiceId ? 'Checking...' : "Check Status (confirm the courier received it)"}
                </button>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={closeBooking} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Skip / Done</button>
                  <button onClick={() => { printLabel(bookedResult); closeBooking(); }} className="btn-confirm" style={{ flex: 1, padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <Printer size={14} /> Print Label
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>Courier Booking — #{bookingSale.invoiceId}</h3>

                <p style={{ fontSize: '12px', color: '#888', fontWeight: 'bold', margin: '0 0 6px' }}>Choose Courier</p>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  {[{ key: 'steadfast', label: 'Steadfast' }, { key: 'pathao', label: 'Pathao' }].map((p) => (
                    <button
                      key={p.key}
                      onClick={() => connected[p.key] && setProvider(p.key)}
                      disabled={!connected[p.key]}
                      style={{
                        flex: 1, padding: '8px', borderRadius: '8px', fontWeight: 'bold',
                        cursor: connected[p.key] ? 'pointer' : 'not-allowed',
                        border: `1px solid ${provider === p.key ? '#4a00e0' : '#ddd'}`,
                        background: provider === p.key ? '#4a00e0' : (connected[p.key] ? '#fff' : '#f4f4f4'),
                        color: provider === p.key ? '#fff' : (connected[p.key] ? '#333' : '#aaa')
                      }}
                    >
                      {p.label}{!connected[p.key] && ' (not connected)'}
                    </button>
                  ))}
                </div>

                {!connected[provider] && (
                  <div style={{ background: '#fff3cd', color: '#8a6d3b', padding: '10px', borderRadius: '8px', marginBottom: '10px', fontSize: '12px' }}>
                    {provider === 'pathao' ? 'Pathao' : 'Steadfast'} isn't connected yet -- add its API key as a Cloudflare Worker secret first (see the notice above).
                  </div>
                )}

                {provider === 'pathao' && connected.pathao && connected.pathaoEnv !== 'live' && (
                  <div style={{ background: '#fff3cd', color: '#8a6d3b', padding: '10px', borderRadius: '8px', marginBottom: '10px', fontSize: '12px' }}>
                    🧪 Test Mode — this booking goes to Pathao's sandbox, not your real account.
                  </div>
                )}

                {error && (
                  <div style={{ background: '#ffeded', color: 'red', padding: '10px', borderRadius: '8px', marginBottom: '10px', fontSize: '13px' }}>
                    {error}
                    {/* "pay your due" is Pathao's own account-level message, not something
                        this app produces -- it's the free credentials every developer in
                        the world shares to test with, so it can carry a leftover "due"
                        from other people's testing with no way for us to clear it. It's
                        not connected to Kizuna's real account or real money either way. */}
                    {connected.pathaoEnv !== 'live' && /due/i.test(error) && (
                      <p style={{ margin: '6px 0 0', fontWeight: 'normal', color: '#8a6d3b' }}>
                        ℹ️ This is Pathao's shared public test account talking, not your real Kizuna account — every developer testing Pathao's API worldwide uses this same login, so it can carry a "due" left over from someone else's testing that we have no way to clear. It doesn't touch your real money or real orders.
                      </p>
                    )}
                  </div>
                )}

                <input className="input-field" placeholder="Customer Name" value={form.recipient_name || ''} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
                <input className="input-field" placeholder="Phone Number" value={form.recipient_phone || ''} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} />
                <input className="input-field" placeholder="Address" value={form.recipient_address || ''} onChange={(e) => setForm({ ...form, recipient_address: e.target.value })} />
                <input
                  className="input-field"
                  type="number"
                  min="0"
                  placeholder="COD Amount (৳)"
                  value={form.cod_amount ?? ''}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setForm({ ...form, cod_amount: Math.max(0, Number(e.target.value) || 0) })}
                />
                {/* Pathao's own docs: "amount_to_collect ... Default should be 0 in case of
                    NON Cash-On-Delivery (COD)". So 0 here is correct and expected when the
                    customer already paid in full -- it is not rejected by Pathao. A negative
                    number (the old mouse-wheel bug could produce one) is what actually fails. */}
                <p style={{ margin: '-6px 0 10px', fontSize: '11px', color: form.cod_amount > 0 ? '#888' : '#218c61', fontWeight: form.cod_amount > 0 ? 'normal' : 'bold' }}>
                  {form.cod_amount > 0
                    ? 'This is what the courier will collect from the customer at delivery.'
                    : '0 = customer already paid in full — Pathao collects nothing on delivery.'}
                </p>
                {modalCodFee > 0 && (
                  <p style={{ margin: '-6px 0 10px', fontSize: '11px', color: '#8a6d3b' }}>
                    Pathao also takes a <b>COD fee of ৳ {formatFee(modalCodFee)}</b> (1% of the COD amount) out of your payout — the customer isn't charged this, it's your cost.
                  </p>
                )}

                {provider === 'pathao' && (
                  connected.pathao ? (
                    <>
                      <select className="input-field" value={form.recipient_city || ''} onChange={(e) => onCityChange(e.target.value)}>
                        <option value="">-- Select City --</option>
                        {cities.map((c) => <option key={c.city_id} value={c.city_id}>{c.city_name}</option>)}
                      </select>
                      <select className="input-field" value={form.recipient_zone || ''} onChange={(e) => onZoneChange(e.target.value)} disabled={!form.recipient_city}>
                        <option value="">-- Select Zone --</option>
                        {zones.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.zone_name}</option>)}
                      </select>
                      <select className="input-field" value={form.recipient_area || ''} onChange={(e) => setForm({ ...form, recipient_area: e.target.value })} disabled={!form.recipient_zone}>
                        <option value="">-- Area (optional) --</option>
                        {areas.map((a) => <option key={a.area_id} value={a.area_id}>{a.area_name}</option>)}
                      </select>
                      {loadingLocation && <p style={{ fontSize: '11px', color: '#999' }}>Loading...</p>}
                      {loadingFee && <p style={{ fontSize: '11px', color: '#999' }}>Checking delivery fee...</p>}
                      {!loadingFee && estimatedFee != null && (
                        <div style={{ fontSize: '12px', color: '#8a6d3b', background: '#fff3cd', padding: '8px 10px', borderRadius: '8px', marginTop: '-4px', lineHeight: '1.6' }}>
                          <b>Pathao will charge you ৳ {formatTk(estimatedFee)} for this delivery.</b>
                          <div>They take it out of the COD money before paying you — the customer is not charged this.</div>
                          {modalCodFee > 0 && (
                            <div style={{ marginTop: '4px' }}>
                              + COD fee ৳ {formatFee(modalCodFee)} = <b>৳ {formatFee(Number(estimatedFee) + modalCodFee)} total to Pathao</b> for this parcel.
                            </div>
                          )}
                          {Number(bookingSale.deliveryCharge) > 0 && bookingSale.deliveryPaidBy !== 'shop' && (
                            <div style={{ marginTop: '4px' }}>
                              You're charging the customer ৳ {formatTk(bookingSale.deliveryCharge)} for delivery, so you keep about
                              <b> ৳ {formatFee(Number(bookingSale.deliveryCharge) - Number(estimatedFee) - modalCodFee)}</b> after Pathao's {modalCodFee > 0 ? 'delivery fee and COD fee' : 'fee'}.
                            </div>
                          )}
                          <div style={{ marginTop: '4px', color: '#c0392b' }}>If the parcel is returned, you still pay this ৳ {formatTk(estimatedFee)}.</div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ fontSize: '11px', color: '#999', marginTop: '-6px' }}>City/Zone/Area will load automatically once Pathao is connected.</p>
                  )
                )}

                {provider === 'steadfast' && (
                  <input className="input-field" placeholder="Note (optional)" value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                )}

                <button onClick={submitBooking} disabled={booking || !connected[provider]} className="btn-confirm" style={{ width: '100%', padding: '12px', marginTop: '8px', opacity: (booking || !connected[provider]) ? 0.6 : 1 }}>
                  {booking ? 'Booking...' : 'Book'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {viewInvoice && (
        <div onClick={() => setViewInvoice(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '25px', width: '450px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative', maxHeight: '95vh', overflowY: 'auto' }}>
            <button onClick={() => setViewInvoice(null)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', zIndex: 10 }}>X</button>

            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
              <h1 style={{ margin: 0, color: '#4a00e0', fontSize: '22px' }}>PB KIZUNA COMPANY LIMITED</h1>
              <p style={{ margin: '4px 0', fontSize: '11px', fontWeight: 'bold' }}>H-10, R-2, Section-2, Block-D, <br />Aftabnagar, Dhaka-1212</p>
              <p style={{ margin: 0, fontSize: '11px' }}>Mobile: 01884-711844</p>
              <div style={{ borderBottom: '1px dashed #000', margin: '12px 0' }}></div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', letterSpacing: '1px' }}>SALES INVOICE</h4>
            </div>

            <div style={{ display: 'flex', gap: '10px', fontSize: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '2px 0' }}><b>Sold By:</b> {viewInvoice.soldBy}</p>
                <p style={{ margin: '2px 0' }}><b>Date:</b> {viewInvoice.dateTime}</p>
                <p style={{ margin: '2px 0' }}><b>Invoice ID:</b> #{viewInvoice.invoiceId}</p>
              </div>
              <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: '8px', padding: '8px 10px', background: '#f8f9ff' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', color: '#4a00e0' }}>Bill To</p>
                <p style={{ margin: '2px 0' }}>{viewInvoice.name || 'Walk-in Customer'}</p>
                {[viewInvoice.phone, viewInvoice.phone2].filter(Boolean).length > 0 && (
                  <p style={{ margin: '2px 0' }}>{[viewInvoice.phone, viewInvoice.phone2].filter(Boolean).join(', ')}</p>
                )}
                {viewInvoice.address && <p style={{ margin: '2px 0' }}>{viewInvoice.address}</p>}
              </div>
            </div>
            <div style={{ borderBottom: '1px dashed #000', margin: '8px 0 12px' }}></div>

            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <th align='left' style={{ paddingBottom: '4px' }}>ITEM</th>
                  <th align='center' style={{ paddingBottom: '4px' }}>QTY</th>
                  <th align='right' style={{ paddingBottom: '4px' }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {(viewInvoice.items || []).map((it, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '5px 0' }}>
                      <b>{it.name}</b>
                      {it.itemDiscAmount > 0 && (
                        <div style={{ fontSize: '10px', color: '#e74c3c' }}>Disc: -৳ {formatTk(it.itemDiscAmount)}</div>
                      )}
                    </td>
                    <td align='center'>{it.qty}</td>
                    <td align='right'>{formatTk(it.finalItemTotal ?? (it.price * it.qty))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ borderTop: '1px solid #eee', marginTop: '10px', paddingTop: '8px', textAlign: 'right', fontSize: '12px' }}>
              {viewInvoice.subtotal > 0 && <p style={{ margin: '3px 0' }}>Sub Total: ৳ {formatTk(viewInvoice.subtotal)}</p>}
              {viewInvoice.totalDiscount > 0 && (
                <p style={{ margin: '3px 0', color: '#e74c3c' }}>Total Discount: -৳ {formatTk(viewInvoice.totalDiscount)}</p>
              )}
              {viewInvoice.deliveryCharge > 0 && (
                viewInvoice.deliveryPaidBy === 'shop' ? (
                  <p style={{ margin: '3px 0', color: '#c0392b' }}>Delivery Charge: ৳ {formatTk(viewInvoice.deliveryCharge)} (paid by {viewInvoice.deliveryPaidByName})</p>
                ) : (
                  <p style={{ margin: '3px 0' }}>Delivery Charge: +৳ {formatTk(viewInvoice.deliveryCharge)}</p>
                )
              )}
              <h3 style={{ margin: '6px 0', fontSize: '14px' }}>Grand Total: ৳ {formatTk(viewInvoice.totalBill)}</h3>
              <p style={{ margin: '3px 0' }}>Paid: ৳ {formatTk(viewInvoice.paid)}</p>
              {Number(viewInvoice.dueAmount || 0) > 0 ? (
                <p style={{ margin: '3px 0', color: '#e74c3c', fontWeight: 'bold' }}>Due: ৳ {formatTk(viewInvoice.dueAmount)}</p>
              ) : (
                <p style={{ margin: '3px 0', color: '#10b981', fontWeight: 'bold' }}>Fully Paid</p>
              )}
            </div>

            <button
              onClick={() => printSalesInvoice(viewInvoice)}
              className="btn-confirm"
              style={{ width: '100%', marginTop: '15px', padding: '10px', fontSize: '13px' }}
            >
              Print Invoice 🖨️
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courier;
